import React, { ChangeEvent, useCallback, useMemo, useState } from 'react';
import i18n from '../../../i18n';
import { WrappedFieldProps } from 'redux-form';
import { Autocomplete } from '@material-ui/lab';
import { TextField } from '@material-ui/core';

export interface AutocompleteControlOption {
    value: string;
    label: string;
}

export interface AutocompleteControlProps {
    label: string;
    items: AutocompleteControlOption[];
    value: string;
    onChange: (value: string) => void;
    // Optional:
    errorMessage?: string;
    isDisabled?: boolean;
    placeholder?: string;
}

export const AutocompleteControl = (props: AutocompleteControlProps) => {
    const { label, items, errorMessage, isDisabled, placeholder, value, onChange } = props;
    const [open, setOpen] = useState(false);
    const selectedOption = useMemo(() => {
        return items.find(item => item.value === value) || null;
    }, [value, items]);
    const getOptionSelected = useCallback((option: AutocompleteControlOption, current: AutocompleteControlOption) => {
        return option.value === current.value;
    }, []);
    const onOptionChange = useCallback((_: ChangeEvent<{}>, option: unknown) => {
        const { value } = option as AutocompleteControlOption;
        onChange(value);
    }, []);
    return (
        <Autocomplete
            open={open}
            onOpen={() => {
                setOpen(true);
            }}
            onClose={() => {
                setOpen(false);
            }}
            getOptionSelected={getOptionSelected}
            getOptionLabel={option => option.label}
            options={items}
            loading={false}
            fullWidth={true}
            size="small"
            disabled={isDisabled}
            value={selectedOption}
            onChange={onOptionChange}
            renderInput={params => (
                <TextField
                    {...params}
                    label={label}
                    variant="filled"
                    disabled={isDisabled}
                    placeholder={placeholder}
                    error={!!errorMessage}
                    helperText={errorMessage}
                    InputProps={{
                        ...params.InputProps,
                        disableUnderline: true,
                    }}
                />
            )}
        />
    );
};

export interface AutocompleteControlFieldOwnProps {
    label: string;
    items: AutocompleteControlOption[];
    // Optional:
    isDisabled?: boolean;
    placeholder?: string;
}

export type AutocompleteControlFieldProps = AutocompleteControlFieldOwnProps & WrappedFieldProps;

export const AutocompleteControlField = (props: AutocompleteControlFieldProps) => {
    const { t } = i18n.useTranslation();
    const {
        input,
        label,
        isDisabled,
        placeholder,
        items,
        meta: { touched, invalid, error },
    } = props;
    return (
        <AutocompleteControl
            label={label}
            items={items}
            errorMessage={touched && invalid ? t(error) : undefined}
            isDisabled={isDisabled}
            placeholder={placeholder}
            {...input}
        />
    );
};
